import { useState, useEffect } from "react";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Play, Clock, ChevronDown, Copy, Check } from "lucide-react";
import { SEO } from "../components/SEO";

const API_BASE = "https://mcp-api.starsinger.ai";

interface EndpointDef {
  id: string;
  label: string;
  path: string;
  params: { name: string; placeholder: string; required?: boolean }[];
}

const ENDPOINTS: EndpointDef[] = [
  {
    id: "search",
    label: "search_music",
    path: "/api/mcp/search",
    params: [
      { name: "q", placeholder: "chill jazz piano", required: true },
      { name: "mood", placeholder: "purple, red, blue, gold..." },
      { name: "limit", placeholder: "10" },
    ],
  },
  {
    id: "trending",
    label: "get_trending",
    path: "/api/mcp/trending",
    params: [{ name: "limit", placeholder: "10" }],
  },
  {
    id: "track",
    label: "get_track",
    path: "/api/mcp/track",
    params: [{ name: "id", placeholder: "Track ID", required: true }],
  },
  {
    id: "artists",
    label: "browse_artists",
    path: "/api/mcp/artists",
    params: [
      { name: "genre", placeholder: "jazz, pop, classical..." },
      { name: "limit", placeholder: "10" },
    ],
  },
  {
    id: "artist",
    label: "get_artist",
    path: "/api/mcp/artist",
    params: [
      { name: "id", placeholder: "Artist ID" },
      { name: "handle", placeholder: "miles_rivers" },
    ],
  },
  {
    id: "moods",
    label: "list_moods",
    path: "/api/mcp/moods",
    params: [],
  },
  {
    id: "playlists",
    label: "list_playlists",
    path: "/api/mcp/playlists",
    params: [
      { name: "tag", placeholder: "chill, morning..." },
      { name: "limit", placeholder: "20" },
    ],
  },
  {
    id: "playlists-public",
    label: "public_playlists",
    path: "/api/mcp/playlists/public",
    params: [
      { name: "tag", placeholder: "morning, gym..." },
      { name: "limit", placeholder: "20" },
    ],
  },
  {
    id: "playlist",
    label: "get_playlist",
    path: "/api/mcp/playlist",
    params: [{ name: "id", placeholder: "Playlist ID", required: true }],
  },
];

export function Playground() {
  const { isAuthenticated } = useConvexAuth();
  const keysData = useQuery(api.mcpPortal.getMyApiKeys);
  const keys = isAuthenticated ? (keysData ?? []) : undefined;

  const [selectedEndpoint, setSelectedEndpoint] = useState(ENDPOINTS[0]);
  const [apiKey, setApiKey] = useState("");
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [response, setResponse] = useState<string | null>(null);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (keys && keys.length > 0 && !apiKey) {
      const activeKey = keys.find((k) => !k.disabled);
      if (activeKey) {
        setApiKey(activeKey.keyPreview);
      }
    }
  }, [keys]);

  const handleEndpointChange = (id: string) => {
    const ep = ENDPOINTS.find((e) => e.id === id)!;
    setSelectedEndpoint(ep);
    setParamValues({});
    setResponse(null);
    setStatusCode(null);
    setDuration(null);
  };

  const buildUrl = () => {
    const url = new URL(selectedEndpoint.path, API_BASE);
    for (const [k, v] of Object.entries(paramValues)) {
      if (v.trim()) url.searchParams.set(k, v.trim());
    }
    return url.toString();
  };

  const buildCurlCommand = () => {
    const url = buildUrl();
    const key = apiKey || "sk_free_...";
    return `curl "${url}" \\\n  -H "Authorization: Bearer ${key}"`;
  };

  const handleSend = async () => {
    if (!apiKey.trim()) {
      setResponse(JSON.stringify({ error: "Please enter your API key" }, null, 2));
      setStatusCode(null);
      return;
    }

    setLoading(true);
    setResponse(null);
    const start = Date.now();

    try {
      const res = await fetch(buildUrl(), {
        headers: { Authorization: `Bearer ${apiKey}` },
      });
      const elapsed = Date.now() - start;
      const body = await res.json();

      setStatusCode(res.status);
      setDuration(elapsed);
      setResponse(JSON.stringify(body, null, 2));
    } catch (err: any) {
      setDuration(Date.now() - start);
      setStatusCode(0);
      setResponse(JSON.stringify({ error: err.message }, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCurl = async () => {
    await navigator.clipboard.writeText(buildCurlCommand());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusColor = (code: number | null) => {
    if (!code) return "text-text-muted";
    if (code >= 200 && code < 300) return "text-green-400";
    if (code >= 400 && code < 500) return "text-yellow-400";
    return "text-red-400";
  };

  return (
    <>
      <SEO
        title="API Playground | StarSinger MCP"
        description="Test StarSinger MCP endpoints directly in your browser. Try the search, trending, and artist APIs with live results."
        pathname="/playground"
      />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">API Playground</h1>
      <p className="text-text-muted mb-8">
        Test StarSinger MCP endpoints directly in your browser.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Request Panel */}
        <div className="space-y-4">
          {/* API Key */}
          <div>
            <label className="block text-xs text-text-muted mb-1.5">API Key</label>
            <input
              type="text"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk_free_..."
              className="w-full px-3 py-2 rounded-lg bg-surface border border-border focus:border-brand focus:outline-none text-sm font-mono"
            />
            {!isAuthenticated && (
              <p className="text-xs text-text-muted mt-1">
                <a href="/login" className="text-brand hover:underline">Sign in</a> to auto-fill your key
              </p>
            )}
          </div>

          {/* Endpoint Selector */}
          <div>
            <label className="block text-xs text-text-muted mb-1.5">Endpoint</label>
            <div className="relative">
              <select
                value={selectedEndpoint.id}
                onChange={(e) => handleEndpointChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-surface border border-border focus:border-brand focus:outline-none text-sm font-mono appearance-none pr-8"
              >
                {ENDPOINTS.map((ep) => (
                  <option key={ep.id} value={ep.id}>
                    {ep.label} — GET {ep.path}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-text-muted absolute right-2.5 top-2.5 pointer-events-none" />
            </div>
          </div>

          {/* Parameters */}
          {selectedEndpoint.params.length > 0 && (
            <div className="space-y-3">
              <label className="block text-xs text-text-muted">Parameters</label>
              {selectedEndpoint.params.map((param) => (
                <div key={param.name} className="flex items-center gap-2">
                  <span className="text-xs font-mono text-text-muted w-16 shrink-0">
                    {param.name}
                    {param.required && <span className="text-red-400">*</span>}
                  </span>
                  <input
                    type="text"
                    value={paramValues[param.name] ?? ""}
                    onChange={(e) =>
                      setParamValues((prev) => ({
                        ...prev,
                        [param.name]: e.target.value,
                      }))
                    }
                    placeholder={param.placeholder}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-surface border border-border focus:border-brand focus:outline-none text-sm"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={loading}
            className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-white py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Play className="w-4 h-4" />
            )}
            {loading ? "Sending..." : "Send Request"}
          </button>

          {/* cURL Preview */}
          <div className="relative">
            <label className="block text-xs text-text-muted mb-1.5">cURL</label>
            <pre className="text-xs font-mono bg-surface border border-border rounded-lg p-3 overflow-x-auto text-text-muted whitespace-pre-wrap">
              {buildCurlCommand()}
            </pre>
            <button
              onClick={handleCopyCurl}
              className="absolute top-7 right-2 p-1.5 rounded-md hover:bg-surface-lighter transition-colors"
              title="Copy cURL command"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <Copy className="w-3.5 h-3.5 text-text-muted" />
              )}
            </button>
          </div>
        </div>

        {/* Response Panel */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs text-text-muted">Response</label>
            {statusCode !== null && (
              <div className="flex items-center gap-3 text-xs">
                <span className={statusColor(statusCode)}>
                  {statusCode === 0 ? "Network Error" : statusCode}
                </span>
                {duration !== null && (
                  <span className="text-text-muted flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {duration}ms
                  </span>
                )}
              </div>
            )}
          </div>
          <pre className="text-xs font-mono bg-surface border border-border rounded-lg p-4 overflow-auto max-h-[600px] min-h-[400px] text-text-muted whitespace-pre-wrap">
            {response ?? "Send a request to see the response here."}
          </pre>
        </div>
      </div>
      </div>
    </>
  );
}
